'use client';

/**
 * The one React Query cache for the whole CMS.
 *
 * The defaults here are the difference between "navigating back to a page shows
 * what was there" and "navigating back re-downloads it". Three of them are
 * deliberate:
 *
 * `staleTime` stays at 30 seconds as the *floor* for anything that does not say
 * otherwise. It is short because most of what the CMS reads is operational —
 * job status, review state, approval verdicts — and showing a five-minute-old
 * approval as current would be worse than a refetch. Resources that genuinely
 * change slowly raise it themselves; see `lib/api/lookups.ts`, where the
 * collection/character/persona lookups every page shares are declared once.
 *
 * `gcTime` is what makes a return visit instant. A query's data is dropped this
 * long after the last component using it unmounts, and the previous default (5
 * minutes) meant a browse through four pages and back had already discarded the
 * first one. Thirty minutes spans a working session; the memory is a few
 * hundred rows of JSON.
 *
 * `refetchOnWindowFocus` is off because it is the single largest source of
 * requests nobody asked for: alt-tabbing to read a ticket and back re-fired
 * every active query on the page. Freshness is governed by `staleTime` and by
 * explicit invalidation after mutations, both of which are answerable questions;
 * "the window regained focus" is not one.
 */

import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

export default function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            gcTime: 30 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      })
  );

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
